import React from "react";
import {
  filterValuesToSearchParams,
  normalizeFilterValues,
  searchParamsToFilterValues,
  type FilterSchema,
  type FilterValues,
} from "./filter-schema.js";

/* ---------------- useFilterUrlState (PB-2941) ----------------
   Applied filter values living in the URL query string: refresh and
   Back/Forward restore them, unrelated parameters survive untouched, invalid
   or retired values fail safe (dropped by the codec), and drafts never write
   — only a commit does. Deliberately router-free (history + popstate), so the
   design system stays decoupled from the host's routing library; the pathname
   never changes here, so a host router's location goes stale on search-only
   writes — unless the host passes `navigate`, which routes the write through
   its router so everything reading the router's location sees it.

   `schema` must be referentially stable (memoized by the consumer) or the
   decoded values churn every render. */

// Local subscribers, notified after our own pushState (which fires no
// popstate); Back/Forward notify through the real popstate event.
const urlListeners = new Set<() => void>();

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener("popstate", onStoreChange);
  urlListeners.add(onStoreChange);
  return () => {
    window.removeEventListener("popstate", onStoreChange);
    urlListeners.delete(onStoreChange);
  };
}

const getSnapshot = (): string => window.location.search;
const getServerSnapshot = (): string => "";

export interface FilterUrlState {
  /** Applied values decoded (and validated) from the current URL. */
  readonly applied: FilterValues;
  /** Commit applied values: pushes ONE history entry when the query string
   * actually changes, so Back undoes the apply. No-op otherwise. */
  readonly setApplied: (next: FilterValues) => void;
}

export interface FilterUrlStateOptions {
  /** The host router's push. Without it the write is a raw `pushState`. */
  readonly navigate?: ((url: string) => void) | undefined;
}

export function useFilterUrlState<Row = unknown>(
  schema: FilterSchema<Row>,
  options: FilterUrlStateOptions = {},
): FilterUrlState {
  const { navigate } = options;
  const search = React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const applied = React.useMemo(
    () => searchParamsToFilterValues(schema, new URLSearchParams(search)),
    [schema, search],
  );

  const setApplied = React.useCallback(
    (next: FilterValues) => {
      const params = filterValuesToSearchParams(
        schema,
        normalizeFilterValues(schema, next),
        new URLSearchParams(window.location.search),
      );
      const nextSearch = params.toString();
      if (nextSearch === window.location.search.replace(/^\?/, "")) return;
      const url = `${window.location.pathname}${
        nextSearch ? `?${nextSearch}` : ""
      }${window.location.hash}`;
      if (navigate) navigate(url);
      else window.history.pushState(window.history.state, "", url);
      for (const listener of urlListeners) listener();
    },
    [navigate, schema],
  );

  return { applied, setApplied };
}
